import React, { useCallback, useEffect, useRef } from 'react';

import { Html } from 'react-konva-utils';
import Konva from 'konva';

import type { TextElement as TextElementType } from 'Components/CanvasEditor/types';

type TextEditorProps = {
  textNode: Konva.Text;
  onClose: VoidFunction;
  onChange: (props: Partial<TextElementType>, id: string) => void;
  onInput: (value: string) => void;
};

export default function TextEditor({ textNode, onClose, onChange, onInput }: TextEditorProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleOutsideClick = useCallback(e => {
    if (textareaRef.current) {
      const textarea = textareaRef.current;
      if (e.target !== textarea) {
        onChange({ text: textarea.value }, textNode.id());
        onClose();
        textarea.blur();
      }
    }
  }, []);

  useEffect(() => {
    if (!textareaRef.current) return;

    const textarea = textareaRef.current;
    const textPosition = textNode.position();
    const areaPosition = {
      x: textPosition.x + textNode.padding(),
      y: textPosition.y + textNode.padding(),
    };

    // Match styles with the text node
    textarea.value = textNode.text();
    textarea.style.position = 'absolute';
    textarea.style.top = `${areaPosition.y}px`;
    textarea.style.left = `${areaPosition.x}px`;
    textarea.style.width = `${textNode.width() - textNode.padding() * 2}px`;
    textarea.style.height = `${textNode.height() - textNode.padding() * 2}px`;
    textarea.style.fontSize = `${textNode.fontSize()}px`;
    textarea.style.border = 'none';
    textarea.style.padding = '0px';
    textarea.style.margin = '0px';
    textarea.style.overflow = 'hidden';
    textarea.style.background = 'none';
    textarea.style.outline = 'none';
    textarea.style.resize = 'none';
    textarea.style.lineHeight = `${textNode.lineHeight()}`;
    textarea.style.fontFamily = textNode.fontFamily();
    textarea.style.transformOrigin = 'left top';
    textarea.style.textAlign = textNode.align();
    textarea.style.color = textNode.fill();

    const rotation = textNode.rotation();
    let transform = '';
    if (rotation) {
      transform += `rotateZ(${rotation}deg)`;
    }
    textarea.style.transform = transform;

    textarea.focus();

    textarea.addEventListener('keydown', function (e): void {
      this.style.height = 'auto';
      this.style.height = `${this.scrollHeight}px`;

      if (e.key === 'Escape') {
        this.blur();
      }
    });

    textarea.addEventListener('input', function () {
      this.style.height = 'auto';
      this.style.height = `${this.scrollHeight}px`;
      onInput(this.value);
    });

    textarea.addEventListener('blur', () => {
      onClose();
    });

    const timeoutId = setTimeout(() => {
      window.addEventListener('click', handleOutsideClick);
    });

    // eslint-disable-next-line consistent-return
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('click', handleOutsideClick);
    };
  }, [textNode, onChange, onClose, onInput, handleOutsideClick]);

  return (
    <Html>
      <textarea
        ref={textareaRef}
        style={{
          position: 'absolute',
        }}
      />
    </Html>
  );
}
